import { api } from "services";

import { isNonEmptyArray, isObject } from "@cayuse/shared-react.utils.type-checks";

import {
	ADMINISTRATION_AD_HOC_TEAM_URL,
	ADMINISTRATION_ORGANIZATION_URL,
	ADMINISTRATION_PERSON_URL,
	IS_DEV
} from "globals/constants";
import store from "store/store";
import { getFieldRestQueryUrl, getStore, notifyError } from "utils";
import { executeRequest, request } from "utils/proxy";

import { getAllActiveAndIncludeRoleByIds } from "./researchTeamRoles";

const INT_PAGE_LIMIT = parseInt(2000);
const INT_PAGE_OFFSET = parseInt(0);
const SORT_DEFAULT_NAME_DESCENDING = "name%2CDESC";

export const findUnitsFromSP = (searchText = "") => {
	return request(`/sp/api/unit?search=${searchText}`)
		.then(newUnits => {
			let units = [];
			if (isNonEmptyArray(newUnits.content)) {
				for (let unit of newUnits.content) {
					units.push({
						text: unit.name,
						value: unit.id,
					});
				}
			}

			return units;
		});
};

export const findOrganizations = (searchText = "") => {
	return request(`/sp/api/organization?search=${searchText}`)
		.then(newOrganizations => {
			let organizations = [];
			if (isNonEmptyArray(newOrganizations.content)) {
				for (let organization of newOrganizations.content) {
					organizations.push({
						...organization,
						text: organization.name,
						value: organization.id,
					});
				}
			}

			return organizations;
		})
		.catch(error => {
			notifyError("Something went wrong. Please try again.", error);

			return [];
		});
};

export const findOrganizationById = id => {
	return executeRequest(`${ADMINISTRATION_ORGANIZATION_URL}/${id}`)
		.then(response => {
			return isObject(response)
				? response
				: null;
		})
		.catch(error => {
			notifyError("Something went wrong. Please try again.", error);

			return null;
		});
};

export const findAdHocTeamById = id => {
	return executeRequest(`${ADMINISTRATION_AD_HOC_TEAM_URL}/${id}`)
		.then(response => {
			return isObject(response) ? response : null;
		})
		.catch(error => {
			if (error.response?.status === 404) {
				return null;
			}

			notifyError("Something went wrong. Please try again.", error);

			return null;
		});
};

export const findUnits = async () => {
	try {
		const response = await api.picklists.findUnit({
			page: INT_PAGE_OFFSET,
			size: INT_PAGE_LIMIT,
			sort: SORT_DEFAULT_NAME_DESCENDING,
		});

		return isNonEmptyArray(response?._embedded?.units)
			? response?._embedded?.units
			: [];
	} catch (error) {
		const message = IS_DEV ? error.message : store.getState().dictionary.common.picklist.cantPullData;
		notifyError(message , error);

		return [];
	}
};

export const findUnitById = async id => {
	try {
		const response = await api.picklists.getUnit(id);

		return isObject(response) ? response : null;
	} catch (error) {
		const message = IS_DEV ? error.message : store.getState().dictionary.common.picklist.cantPullData;
		notifyError(message , error);

		return null;
	}
};

export const findTeams = () => {
	return executeRequest(`${ADMINISTRATION_AD_HOC_TEAM_URL}?page=${INT_PAGE_OFFSET}&size=${INT_PAGE_LIMIT}&sort=${SORT_DEFAULT_NAME_DESCENDING}`)
		.then(response => {
			return isNonEmptyArray(response?._embedded?.adhocteams)
				? response?._embedded?.adhocteams
				: [];
		})
		.catch(async error => {
			const store = await getStore();
			const dictionary = store.getState().dictionary.common;
			const message = IS_DEV ? error.message : dictionary.picklist.cantPullData;
			notifyError(message , error);

			return [];
		});
};

export const findPeople = args => {
	const params = {
		pageOffset: INT_PAGE_OFFSET,
		pageLimit: INT_PAGE_LIMIT,
		searchTerms: [],
		...args
	};

	const stringifySearch = searchTerms => {
		let string = "";
		if(isNonEmptyArray(searchTerms)) {
			searchTerms.forEach((search, index) => {
				const shouldAddComma = index !== (searchTerms.length - 1);
				string = `${string}${search.key}:${search.val}${shouldAddComma ? "," : ""}`;
			});
		}

		return string;
	};

	const page = `page=${params.pageOffset}`;
	const size = `size=${params.pageLimit}`;
	const sort = "sort=firstName%2ClastName%2CASC";
	const searchQuery = `search=${stringifySearch(params.searchTerms)}`;
	const url = isNonEmptyArray(params.searchTerms) ?
	`${ADMINISTRATION_PERSON_URL}?${page}&${size}&${sort}&${searchQuery}` :
	`${ADMINISTRATION_PERSON_URL}?${page}&${size}&${sort}`;

	return executeRequest(url)
		.then(response => {
			return isNonEmptyArray(response?._embedded?.persons)
				? response?._embedded?.persons
				: [];
		})
		.catch(async error => {
			const store = await getStore();
			const dictionary = store.getState().dictionary.common;
			const message = IS_DEV ? error.message : dictionary.picklist.cantPullData;
			notifyError(message, error);

			return [];
		});
};

export const findPersonById = id => {
	return executeRequest(`${ADMINISTRATION_PERSON_URL}/${id}`)
		.then(response => {
			return isObject(response)
				? response
				: null;
		})
		.catch(async error => {
			const store = await getStore();
			const dictionary = store.getState().dictionary.common;
			const message = IS_DEV ? error.message : dictionary.picklist.cantPullData;
			notifyError(message , error);

			return null;
		});
};

export const findPicklistOptionsByFieldId = field => {
	if (field.dataSource) {
		if (field.dataSource.type === "Static") {
			return new Promise(resolve => {
				resolve(field.dataSource.staticValues);
			})
				.then(data => {
					return {
						options: data.map(optionValue => {
							return {
								id: optionValue,
								label: optionValue
							};
						})
					};
				});
		} else if (field.dataSource.type === "Rest") {
			const queryUrl = getFieldRestQueryUrl(field);

			return executeRequest(queryUrl)
				.then(res => {
					if (!isObject(res)) {
						return [];
					}

					const data = res.content
						? res.content
						: res;

					return {
						options: data.map(searchValue => {
							return {
								id: searchValue.id,
								label: searchValue.name // TODO disposition based on dataSource "restFormatItem"? defer to Jon
							};
						})
					};
				})
				.catch(async error => {
					const store = await getStore();
					const dictionary = store.getState().dictionary.common;
					const message = IS_DEV ? error.message : dictionary.picklist.cantPullData;
					notifyError(message, error);

					return null;
				});
		}
	} else {
		console.warn("unrecognized dataSource provided in form for fieldId: ", field.id);

		return new Promise(resolve => {
			setTimeout(() => resolve([]), 500);
		})
			.then(options => {
				return {
					options: options
				};
			});
	}
};

export const findPicklistOptionByFieldIdAndOptionId = (field, targetId) => {
	return findPicklistOptionsByFieldId(field) // TODO weird data-binding issue that poisons state? defer to Jon
		.then(data => {
			return data.options.find(option => option.id === targetId);
		});
};

export const getResearchTeamRoles = async ({ ids } = {}) => {
	try {
		const response = await getAllActiveAndIncludeRoleByIds(ids);

		return response.items
			.map(role => ({
				position: role.position,
				id: role.researchRoleId,
				label: role.name,
				text: role.name,
			  active: role.active,
			}));
	} catch (error) {
		const state = store.getState();
		const message = IS_DEV ? error.message : state.dictionary.common.picklist.cantPullData;
		notifyError(message , error);
	}
};
